import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JobCompletionChecklist } from '@conversia/shared-types';
import * as jobsApi from '@/api/jobs.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';

/** Ítems del checklist de cierre (mismos del ponche "Terminé" del técnico). */
const CHECKLIST_ITEMS: { key: keyof JobCompletionChecklist; label: string }[] = [
  { key: 'productoInstalado', label: 'Producto instalado' },
  { key: 'sinProblemaPuerta', label: 'Sin problema con la puerta' },
  { key: 'areaLimpia', label: 'Área limpia' },
  { key: 'probada', label: 'Probada' },
];

const EMPTY_CHECKLIST: JobCompletionChecklist = {
  productoInstalado: false,
  sinProblemaPuerta: false,
  areaLimpia: false,
  probada: false,
};

/**
 * Reporte de cierre EDITABLE (Fase 2): el admin corrige lo que el técnico
 * reportó al cerrar (resultado, motivo, checklist, registro del producto).
 * Distinto de editar la FICHA: esto es el reporte. Queda en el historial con
 * antes/después.
 */
export function EditarCierreModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [resultado, setResultado] = useState('');
  const [motivo, setMotivo] = useState('');
  const [checklist, setChecklist] = useState<JobCompletionChecklist>(EMPTY_CHECKLIST);
  const [error, setError] = useState<string | null>(null);
  const [registro, setRegistro] = useState('');

  // Pre-carga el reporte actual al abrir (job cambia de null → trabajo).
  useEffect(() => {
    if (!job) return;
    setResultado(job.resultado ?? '');
    setMotivo(job.motivo ?? '');
    setRegistro(job.registroProducto ?? '');
    setChecklist(job.completionChecklistJson ?? EMPTY_CHECKLIST);
    setError(null);
  }, [job]);

  const save = useMutation({
    mutationFn: () =>
      jobsApi.updateCierre(job!.id, {
        resultado: resultado.trim() || null,
        motivo: motivo.trim() || null,
        checklist,
        registroProducto: registro.trim() || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Reporte de cierre actualizado');
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar el reporte'),
  });

  const esNoCompletado = job?.estado === 'NO_COMPLETADO';

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title="Editar reporte de cierre"
      description="Corrige lo que reportó el técnico al cerrar. El cambio queda en el historial (quién, cuándo y qué)."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          label="Resultado"
          placeholder="Qué se hizo / cómo quedó el trabajo…"
          rows={2}
          value={resultado}
          onChange={(e) => setResultado(e.target.value)}
        />
        {esNoCompletado && (
          <Textarea
            label="Motivo (No completado)"
            placeholder="Por qué no se completó…"
            rows={2}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        )}
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Verificación
          </legend>
          {CHECKLIST_ITEMS.map((item) => (
            <label
              key={item.key}
              className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={checklist[item.key]}
                onChange={(e) => setChecklist((c) => ({ ...c, [item.key]: e.target.checked }))}
              />
              {item.label}
            </label>
          ))}
        </fieldset>
        <Input
          label="Datos de registro del producto"
          placeholder="Código / serie que indicó el cliente"
          value={registro}
          onChange={(e) => setRegistro(e.target.value)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
